// Lead Generation Interaction Types
export const LEAD_GENERATION_INTERACTION_TYPES = {
  ENROLLMENT_CTA_TAPPED: 'INTERACTION_TYPE_ENTER_ENROLLMENT_CTA_TAPPED',
  ENGAGEMENT_LINK_TAPPED: 'INTERACTION_TYPE_ENGAGEMENT_LINK_TAPPED',
  INTERNAL_LINK_TAPPED: 'INTERACTION_TYPE_INTERNAL_LINK_TAPPED',
  OUTBOUND_LINK_TAPPED: 'INTERACTION_TYPE_OUTBOUND_LINK_TAPPED',
  MODULE_ELEMENT_TAPPED: 'INTERACTION_TYPE_MODULE_ELEMENT_TAPPED',
  BLOG_QUIZ_INTERACTION: 'INTERACTION_TYPE_BLOG_QUIZ_INTERACTION',
  NAVIGATION_BAR_INTERACTION: 'INTERACTION_TYPE_NAVIGATION_BAR_INTERACTION',
  SECONDARY_CTA_CLICKED: 'INTERACTION_TYPE_SECONDARY_CTA_CLICKED',
  BLOG_QUIZ: 'INTERACTION_TYPE_BLOG_QUIZ',
  PROMO_CODE_ENTERED: 'INTERACTION_TYPE_PROMO_CODE_ENTERED',
  FEATURE_BANNER_CTA_CLICKED: 'INTERACTION_TYPE_FEATURE_BANNER_CTA_CLICKED',
  EXPERIMENT_VIEWED: 'INTERACTION_TYPE_EXPERIMENT_VIEWED',
} as const;

// This creates a type from the values of the const object
export type LeadGenerationInteractionType =
  (typeof LEAD_GENERATION_INTERACTION_TYPES)[keyof typeof LEAD_GENERATION_INTERACTION_TYPES];

export interface TrackingAttribute {
  key: string;
  value: string | number | undefined;
}

export interface InteractionDetails {
  location: string;
  action_detail: string;
  email_entered?: boolean;
}

// Define tracking function type
export type TrackingFunction = (props?: Record<string, unknown>, callback?: () => void) => void;

// Consolidated Lead Generation Props
export interface LeadGenerationInteractionProps {
  interaction_type: LeadGenerationInteractionType;
  interaction_details: InteractionDetails;
  // Root level properties for enrollment events
  location?: string;
  button_location?: string;
  email_entered?: boolean;
  email?: string;
  ignored_qsps?: Record<string, unknown>;
  user_alias?: {
    alias_label: string;
    alias_name: Record<string, unknown>;
  };
  additional_data?: TrackingAttribute[];
}

// Other standalone events
export interface FAQArrowClickedProps {
  faq_question?: string;
}

export interface TocToggleClickedEvent {
  action: 'open' | 'close';
}

export interface TocAnchorClickedEvent {
  anchor_label: string;
}

export interface ExpandableCardItemToggledEvent {
  action: 'open' | 'close';
}

export interface VisitorIDSetProps {
  segment_anonymous_id?: string;
}

export interface LoginLinkTappedProps {
  cta_copy?: string;
  lp_type?: string;
}

export interface ScrollDepthProps {
  depth_percentage: number;
}

export const ANALYTICS_EVENTS = {
  LEAD_GENERATION_INTERACTION: 'Lead Generation Interaction Captured',
  FAQ_ARROW_CLICKED: 'FAQ Arrow Clicked',
  VISITOR_ID_SET: 'Visitor ID Set',
  LANDING_PAGE_EMAIL_FILLED: 'Landing Page Email Filled',
  LOGIN_LINK_TAPPED: 'Login Link Tapped',
  MODULE_VIEWED: 'Module Viewed',
  SLIDER_BAR_CLICKED: 'Slider Bar Clicked',
  FLOATING_DISCLAIMER_SHOWN: 'Floating Disclaimer Shown',
  ACCORDION_CLICKED: 'Accordion Clicked',
  VIDEO_INTERACTION: 'Video Interaction',
  SCROLL_DEPTH: 'Scroll Depth',
} as const;
// Union type for all possible analytics properties
export type AnalyticsProperties =
  | LeadGenerationInteractionProps
  | FAQArrowClickedProps
  | ExpandableCardItemToggledEvent
  | LoginLinkTappedProps
  | TocToggleClickedEvent
  | TocAnchorClickedEvent
  | VisitorIDSetProps
  | ScrollDepthProps
  | Record<string, never>;

// Type for the analytics hook function

export type AnalyticsHook = (props: AnalyticsProperties, autoTrack?: boolean) => TrackingFunction;
